import { useEffect, useRef } from 'react';
import { fetchExperience } from 'actions/experience';

import { useAppState, useBrandId, useDispatch } from 'AppProvider';
import useExperienceId from 'hooks/use-experience-id';
import useSearchQueryParams from 'hooks/use-search-params';
import useSupportedLanguage from 'utils/data/language/useSupportedLanguage';

const useFetchExperience = () => {
  const dispatch = useDispatch();
  const experienceId = useExperienceId();
  const brandId = useBrandId();
  const { searchParams } = useSearchQueryParams();
  const languageCode = useSupportedLanguage();

  const latest = String(searchParams?.latest).toLowerCase() === 'true';

  const {
    experience,
    error,
    fetchExperienceStatus,
    fetchExperienceResponseCode,
    fetchSettingsStatus,
    experienceSettings,
  } = useAppState((state) => state.experience);

  // Tracks the most recently issued request so a slower, superseded response
  // (e.g. redirect fires a new fetch before the previous one resolves) can't
  // overwrite the store with stale data once it eventually arrives.
  const latestRequestRef = useRef();

  // Re-fetch whenever the route's experience, the `latest` flag, or the language
  // changes. These come from react-router (`useParams`) / i18n, so the effect fires
  // on forward/back and in-app navigation in every router mode — unlike the previous
  // `window.location.hash` key, which never changes outside hash mode.
  useEffect(() => {
    if (experienceId) {
      const requestToken = Symbol(experienceId);
      latestRequestRef.current = requestToken;
      dispatch(
        fetchExperience(experienceId, {
          brandId,
          latest,
          languageCode,
          isStale: () => latestRequestRef.current !== requestToken,
        }),
      );
    }
  }, [experienceId, latest, languageCode]);

  return {
    experience,
    error,
    fetchExperienceStatus,
    responseStatus: fetchExperienceResponseCode,
    fetchSettingsStatus,
    experienceSettings,
  };
};

export default useFetchExperience;
